import {
  SelectWithFilter,
  Option,
  Loading,
} from '@trufla-technology/trumarket-react-design-system';
import { userSelectList } from 'app/types/User';
import { useGetTeams } from '@trufla-technology/trufla-trudomain/dist/lib/truMarket';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GetTeamsResponse } from '@trufla-technology/trufla-trudomain/dist/lib/truMarket/types';
import { debounce } from 'lodash';

type Team = Extract<GetTeamsResponse['data'], object>[number];

export const TeamSelect = ({ value, onChange }: userSelectList<string[]>) => {
  const handleChange = (v: string[]) => {
    onChange?.(v);
  };
  const initialRender = useRef({ loadMore: true });
  const [teams, setTeams] = useState<GetTeamsResponse['data']>([]);
  const { data, loading, handleFilter, paging, handleOffset, query } =
    useGetTeams({
      select: ['key', 'name'],
      paginate: { limit: 10, offset: 0 },
    });

  useEffect(() => {
    if (!data || data.length < 1) {
      return;
    }
    setTeams([...teams!, ...data]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const loadMore = useCallback(() => {
    if (initialRender.current.loadMore)
      return (initialRender.current.loadMore = false);
    if (teams?.length === paging?.count) return;
    return setTimeout(() => {
      handleOffset(query.paginate?.limit! + query.paginate?.offset!);
    }, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, teams]);

  const handleSearch = debounce((value: string) => {
    setTeams([]);
    if (!value) {
      handleFilter({ name: undefined }, { override: true });
      return;
    }
    const searchValue = value?.trim()?.toLowerCase();
    handleFilter({ name: searchValue });
  }, 500);

  const handleScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { scrollTop, clientHeight, scrollHeight } =
      event.target as HTMLElement;
    const threshold = 1;

    const isBottom =
      Math.abs(scrollTop - (scrollHeight - clientHeight)) < threshold;

    if (loading || !isBottom) return;
    loadMore();
  };

  return (
    <SelectWithFilter
      value={value || []}
      mode="multiple"
      onChange={handleChange}
      onSearch={handleSearch}
      onPopupScroll={handleScroll}
      virtual={false}
    >
      {loading ? (
        <Option value="" disabled>
          <Loading size="small" />
        </Option>
      ) : teams?.length ? (
        teams?.map(({ key, name }: Team) => (
          <Option key={`select-option-${key}`} value={key} title={name}>
            <span>{name}</span>
          </Option>
        ))
      ) : (
        <Option value="" disabled>
          <span>No Data Found</span>
        </Option>
      )}
    </SelectWithFilter>
  );
};
